"use client"
import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import GoToLink from "@/components/shared/GoToLink";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import "@/components/social/SocialLinks.css";

const SocialLinks = ({
  ownerId,
  contentId,
  socialProfile,
  classNameOverride,
  containerClassNameOverride,
  omitWebsite,
  condensed = true
}) => {
  const [orderedLinks, setOrderedLinks] = useState([]);

  useEffect(() => {
    if (socialProfile) {
      const sortedLinks = socialProfile.sort(
        (a, b) =>
          defaultLinks.find(dl => dl.type === a.type)?.order -
          defaultLinks.find(dl => dl.type === b.type)?.order
      );
      setOrderedLinks(sortedLinks);
    }
  }, [socialProfile]);

  const defaultLinks = [
    { type: "WEBSITE", label: "Website", icon: "fa-solid fa-globe", order: 1 },
    {
      type: "ECOMMERCE",
      label: "Ecommerce/Shop",
      icon: "fa-solid fa-cart-plus",
      order: 2
    },
    { type: "MENU", label: "Menu", icon: "fa-solid fa-fork-knife", order: 3 },
    {
      type: "TICKETS",
      label: "Tickets/Sales",
      icon: "fa-solid fa-ticket",
      order: 4
    },
    {
      type: "CALENDAR",
      label: "Calendar/Schedule",
      icon: "fa-solid fa-calendars",
      order: 5
    },
    {
      type: "FACEBOOK",
      label: "Facebook",
      icon: "fa-brands fa-facebook",
      order: 6
    },
    {
      type: "TWITTER",
      label: "Twitter",
      icon: "fa-brands fa-twitter",
      order: 7
    },
    {
      type: "INSTAGRAM",
      label: "Instagram",
      icon: "fa-brands fa-instagram",
      order: 8
    },
    { type: "TIKTOK", label: "TikTok", icon: "fa-brands fa-tiktok", order: 9 },
    {
      type: "YOUTUBE",
      label: "YouTube",
      icon: "fa-brands fa-youtube",
      order: 10
    },
    {
      type: "LINKEDIN",
      label: "LinkedIn",
      icon: "fa-brands fa-linkedin",
      order: 11
    },
    {
      type: "PINTEREST",
      label: "Pinterest",
      icon: "fa-brands fa-pinterest",
      order: 12
    },
    {
      type: "VENMO",
      label: "Cash App",
      icon: "fa-solid fa-circle-dollar",
      order: 13
    },
    { type: "YELP", label: "Yelp", icon: "fa-brands fa-yelp", order: 14 }
  ];

  var _containerClassNameOverride = containerClassNameOverride;
  if (_containerClassNameOverride == null) {
    _containerClassNameOverride = "social-container";
  }
  var _classNameOverride = classNameOverride;
  if (_classNameOverride == null) {
    _classNameOverride = "social-link";
  }

  const urlFilter = (value, type) => {
    if (isUrl(value)) {
      return addProtocolIfMissing(value);
    } else {
      if (type === "INSTAGRAM") {
        return `https://www.instagram.com/${value}`;
      } else if (type === "FACEBOOK") {
        return `https://www.facebook.com/${value}`;
      } else {
        return addProtocolIfMissing(value);
      }
    }
  };

  const isUrl = url => {
    if (url) {
      var expression = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}(\/.*)?$/;
      var regex = new RegExp(expression);
      return url.match(regex);
    } else {
      return false;
    }
  };

  const addProtocolIfMissing = url => {
    // Regular expression to check if the URL has a valid protocol at the start
    const protocolRegex = /^(https:\/\/|http:\/\/)/i;

    // If the URL doesn't have a valid protocol, prepend 'https://' by default
    if (!protocolRegex.test(url)) {
      url = "https://" + url;
    }

    return url;
  };

  const renderSocialLink = sp => (
    <span className="text-color">
      {ownerId ? (
        <GoToLink
          classProps={_classNameOverride}
          ownerId={ownerId}
          contentId={contentId ?? ownerId}
          type={sp.type}
          url={urlFilter(sp.value, sp.type)}
          toRender={
            <>
              <i
                className={`text-color ${
                  defaultLinks.find(dl => dl.type === sp.type)?.icon
                }`}
              ></i>
              {!condensed && (
                <span className="text-color">
                  {defaultLinks.find(dl => dl.type === sp.type)?.label}
                </span>
              )}
            </>
          }
        />
      ) : (
        <a
          className="text-color with-icon"
          href={sp.value}
          target="_blank"
          rel="noopener noreferrer"
          onClick={evt => evt.stopPropagation()}
        >
          <i
            className={`${defaultLinks.find(dl => dl.type === sp.type)?.icon}`}
          ></i>
          {!condensed && (
            <span>{defaultLinks.find(dl => dl.type === sp.type)?.label}</span>
          )}
        </a>
      )}
    </span>
  );

  return (
    <div
      className={`${_containerClassNameOverride} ${
        condensed ? "condensed" : "expanded"
      }`}
    >
      {orderedLinks &&
        orderedLinks.map((sp, index) => {
          if (sp.value == null) {
            return <Fragment key={index}></Fragment>;
          } else if (sp.type === "WEBSITE" && omitWebsite) {
            return <Fragment key={`${sp.type}-${index}`}></Fragment>;
          } else {
            if (!condensed) {
              return <Fragment key={`${sp.type}-${index}`}>{renderSocialLink(sp)}</Fragment>;
            }

            return (
              <OverlayTrigger
                key={`${sp.type}-${index}`}
                placement="bottom"
                overlay={
                  <Tooltip id={`tooltip-${sp.type}`}>
                    {defaultLinks.find(dl => dl.type === sp.type)?.label}
                  </Tooltip>
                }
              >
                {renderSocialLink(sp)}
              </OverlayTrigger>
            );
          }
        })}
    </div>
  );
};
SocialLinks.propTypes = {
  ownerId: PropTypes.string,
  socialProfile: PropTypes.array,
  classNameOverride: PropTypes.string,
  omitWebsite: PropTypes.bool
};
export default SocialLinks;
